import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { downloadInvoice } from '../../utils/invoiceDownload';
import InvoicePreview from '../shared/InvoicePreview';
import EarlyPaymentBanner from '../shared/EarlyPaymentBanner';
import { getDisplayAmount } from '../../utils/amountUtils';
import {
  Search,
  Filter,
  Eye,
  Download,
  FileText,
  Trash2,
  Send,
  Bell,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  DollarSign,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  RefreshCw,
  Paperclip,
} from 'lucide-react';

const SentInvoices = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [amountRange, setAmountRange] = useState({ min: '', max: '' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [actionModal, setActionModal] = useState({ open: false, type: '', invoice: null });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [invoicesPerPage] = useState(10);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [reminderCooldowns, setReminderCooldowns] = useState({});
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [highlightedInvoiceId, setHighlightedInvoiceId] = useState(null);

  // Fetch real data from backend
  useEffect(() => {
    if (user) {
      fetchSentInvoices();
    }
  }, [user]);

  // Check for highlight parameter to highlight specific invoice
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightedInvoiceId(highlightId);
      // Auto-remove highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedInvoiceId(null);
        // Remove highlight parameter from URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('highlight');
        setSearchParams(newParams, { replace: true });
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  // Listen for notification clicks to auto-open invoice preview
  useEffect(() => {
    const handleOpenInvoicePreview = (event) => {
      const { invoiceId } = event.detail;
      if (invoiceId && invoices.length > 0) {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        if (invoice) {
          setPreviewInvoice(invoice);
          setShowPreview(true);
          setHighlightedInvoiceId(invoiceId);
          
          // Remove highlight after 5 seconds
          setTimeout(() => setHighlightedInvoiceId(null), 5000);
        }
      }
    };

    window.addEventListener('openInvoicePreview', handleOpenInvoicePreview);
    return () => window.removeEventListener('openInvoicePreview', handleOpenInvoicePreview);
  }, [invoices]);

  const fetchSentInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/invoices?role=sender');
      
      if (response.data.data && response.data.data.invoices) {
        // Debug: Log raw invoice data from API
        console.log('📊 SentInvoices - Raw API data:', response.data.data.invoices.map(inv => ({
          invoiceNumber: inv.invoiceNumber,
          discounts: inv.discounts,
          earlyPaymentDiscounts: inv.earlyPaymentDiscounts
        })));
        
        // 🔍 DEBUG: Check refund data for INV-2025-8589
        const invoice8589 = response.data.data.invoices.find(inv => inv.invoiceNumber === 'INV-2025-8589');
        if (invoice8589) {
          console.log('🔍 SENT INV-2025-8589 FULL DATA:', {
            invoiceNumber: invoice8589.invoiceNumber,
            status: invoice8589.status,
            refundStatus: invoice8589.refundStatus,
            refundAmount: invoice8589.refundAmount,
            refundDate: invoice8589.refundDate,
            refundReason: invoice8589.refundReason,
            allKeys: Object.keys(invoice8589)
          });
        }

        // Transform backend data to match frontend structure
        const transformedInvoices = response.data.data.invoices.map(invoice => ({
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          invoiceId: invoice.invoiceId, // Short code from backend (do NOT overwrite with UUID!)
          guestLookupCode: invoice.guestLookupCode, // Short 8-char code for guests
          isGuestInvoice: !invoice.receiverId && invoice.guestRecipient,
          guestRecipient: invoice.guestRecipient,
          creator: invoice.creator ? {
            id: invoice.creator.id,
            firstName: invoice.creator.firstName,
            lastName: invoice.creator.lastName,
            initials: `${invoice.creator.firstName?.[0] || ''}${invoice.creator.lastName?.[0] || ''}`.toUpperCase()
          } : null,
          // For InvoicePreview component - sender info (use invoice.sender from backend with full address)
          sender: invoice.sender ? {
            companyName: invoice.sender.companyName,
            firstName: invoice.sender.firstName,
            lastName: invoice.sender.lastName,
            email: invoice.sender.email,
            address: invoice.sender.address,
            city: invoice.sender.city,
            state: invoice.sender.state,
            zipCode: invoice.sender.zipCode,
            country: invoice.sender.country,
            subscriptionPlan: invoice.sender.subscriptionPlan
          } : {
            // Fallback to user object if invoice.sender not available
            companyName: user?.companyName,
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
            address: user?.address,
            city: user?.city,
            state: user?.state,
            zipCode: user?.zipCode,
            country: user?.country,
            subscriptionPlan: user?.subscriptionPlan
          },
          // For InvoicePreview component - receiver info (who received the invoice)
          receiver: invoice.receiver ? {
            companyName: invoice.receiver.companyName,
            firstName: invoice.receiver.firstName,
            lastName: invoice.receiver.lastName,
            email: invoice.receiver.email,
            address: invoice.receiver.address,
            city: invoice.receiver.city,
            state: invoice.receiver.state,
            zipCode: invoice.receiver.zipCode,
            country: invoice.receiver.country,
            subscriptionPlan: invoice.receiver.subscriptionPlan
          } : null,
          // For table display - customer info
          customer: {
            name: invoice.receiverId 
              ? (invoice.receiver?.companyName || `${invoice.receiver?.firstName || ''} ${invoice.receiver?.lastName || ''}`.trim())
              : (invoice.guestRecipient?.companyName || `${invoice.guestRecipient?.firstName || ''} ${invoice.guestRecipient?.lastName || ''}`.trim() || invoice.guestRecipient?.email),
            email: invoice.receiver?.email || invoice.guestRecipient?.email,
            address: invoice.receiver?.address || invoice.guestRecipient?.address
          },
        amount: getDisplayAmount(invoice.total),
          status: invoice.status,
          paymentDate: invoice.paymentDate || invoice.payment_date,
          dueDate: invoice.dueDate,
          sentDate: invoice.createdAt || invoice.created_at,
          createdAt: invoice.createdAt || invoice.created_at,
          description: invoice.notes || 'No description provided',
          lineItems: invoice.items || [],
          subtotal: invoice.subtotal, // Map subtotal from backend
          tax: invoice.tax, // Map tax from backend
          notes: invoice.notes,
          terms: invoice.terms,
          poNumber: invoice.poNumber,
          attachments: invoice.metadata?.attachments || [],
          paymentHistory: [],
          rejectionReason: invoice.rejectionReason, // Fixed field name to match InvoicePreview
          disputeReason: invoice.disputeReason,
          isGuest: !invoice.receiverId,
          lastReminderSent: invoice.lastReminderSent || null,
          discounts: invoice.discounts || invoice.earlyPaymentDiscounts || [], // Map backend discounts field (early payment offers)
          // Map applied discount fields
          discountAmount: invoice.discountAmount,
          appliedDiscountId: invoice.appliedDiscountId,
          discountSourceInvoiceId: invoice.discountSourceInvoiceId,
          discountSourceInvoice: invoice.discountSourceInvoice,
          // Map refund fields
          refundStatus: invoice.refundStatus,
          refundAmount: invoice.refundAmount,
          refundDate: invoice.refundDate,
          refundReason: invoice.refundReason,
          stripeRefundId: invoice.stripeRefundId
        }));
        
        setInvoices(transformedInvoices);
        setFilteredInvoices(transformedInvoices);
        
        // Initialize reminder cooldowns
        const cooldowns = {};
        transformedInvoices.forEach(invoice => {
          if (invoice.lastReminderSent) {
            const lastReminder = new Date(invoice.lastReminderSent);
            const now = new Date();
            const timeDiff = now - lastReminder;
            const daysSinceReminder = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            cooldowns[invoice.id] = Math.max(0, 5 - daysSinceReminder);
          }
        });
        setReminderCooldowns(cooldowns);
        
        // If no invoices returned from API, show empty state
        if (transformedInvoices.length === 0) {
          // No invoices from API, showing empty state
          setInvoices([]);
          setFilteredInvoices([]);
        }
      } else {
        console.error('Failed to fetch invoices:', response.data.message);
        setInvoices([]);
        setFilteredInvoices([]);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
      setFilteredInvoices([]);
    } finally {
      setLoading(false);
    }
  };


  // Search and filter functionality
  useEffect(() => {
    let filtered = invoices;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(invoice => {
        const invoiceNumber = invoice.invoiceNumber?.toLowerCase() || '';
        const customerName = invoice.customer?.name?.toLowerCase() || '';
        const customerCompany = invoice.customer?.companyName?.toLowerCase() || '';
        const customerEmail = invoice.customer?.email?.toLowerCase() || '';
        
        return invoiceNumber.includes(query) ||
               customerName.includes(query) ||
               customerCompany.includes(query) ||
               customerEmail.includes(query);
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(invoice => {
        if (statusFilter === 'pending') {
          // 'pending' filter matches 'pending_approval' status
          return invoice.status === 'pending_approval';
        } else if (statusFilter === 'auto-approved') {
          // Handle both 'auto-approved' (filter) and 'auto_approved' (actual status)
          return invoice.status === 'auto_approved' || invoice.approvalStatus === 'auto_approved';
        }
        return invoice.status === statusFilter;
      });
    }

    // Date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(invoice => {
        const dueDate = new Date(invoice.dueDate);
        const start = dateRange.start ? new Date(dateRange.start) : null;
        const end = dateRange.end ? new Date(dateRange.end) : null;
        
        if (start && end) {
          return dueDate >= start && dueDate <= end;
        } else if (start) {
          return dueDate >= start;
        } else if (end) {
          return dueDate <= end;
        }
        return true;
      });
    }

    // Amount range filter
    if (amountRange.min || amountRange.max) {
      filtered = filtered.filter(invoice => {
        const amount = invoice.amount;
        const min = amountRange.min ? parseFloat(amountRange.min) : 0;
        const max = amountRange.max ? parseFloat(amountRange.max) : Infinity;
        
        return amount >= min && amount <= max;
      });
    }

    setFilteredInvoices(filtered);
  }, [invoices, searchQuery, statusFilter, dateRange, amountRange]);

  // Pagination logic
  const totalPages = Math.ceil(filteredInvoices.length / invoicesPerPage);
  const startIndex = (currentPage - 1) * invoicesPerPage;
  const endIndex = startIndex + invoicesPerPage;
  const currentInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, dateRange, amountRange]);

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredInvoices].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (key === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (key === 'dueDate' || key === 'sentDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredInvoices(sorted);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDateRange({ start: '', end: '' });
    setAmountRange({ min: '', max: '' });
    setCurrentPage(1);
  };

  // Handle invoice selection
  const handleInvoiceSelect = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedInvoices.length === currentInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(currentInvoices.map(invoice => invoice.id));
    }
  };

  // Export selected invoices as CSV
  const handleExportSelected = () => {
    try {
      if (selectedInvoices.length === 0) {
        alert('Please select invoices to export');
        return;
      }

      const selectedInvoiceData = invoices.filter(invoice => 
        selectedInvoices.includes(invoice.id)
      );

      if (selectedInvoiceData.length === 0) {
        alert('No invoice data found for export');
        return;
      }

      // Create CSV content with proper escaping
      const csvContent = [
        ['Invoice Number', 'Invoice ID', 'Customer', 'Amount', 'Status', 'Due Date', 'Sent Date', 'Description'],
        ...selectedInvoiceData.map(invoice => [
          invoice.invoiceNumber || '',
          invoice.isGuestInvoice ? (invoice.invoiceId || '') : '—',
          invoice.customer?.name || '',
          `£${(parseFloat(invoice.amount) || 0).toFixed(2)}`,
          (invoice.status || '').replace(/-/g, ' ').toUpperCase(),
          invoice.dueDate ? formatDate(invoice.dueDate) : '',
          invoice.sentDate ? formatDate(invoice.sentDate) : '',
          (invoice.description || '').replace(/"/g, '""') // Escape quotes
        ])
      ].map(row => 
        row.map(cell => `"${cell}"`).join(',')
      ).join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sent-invoices-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Clear selection
      setSelectedInvoices([]);
      alert(`Successfully exported ${selectedInvoiceData.length} invoice(s) to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export invoices. Please try again.');
    }
  };

  // Export selected invoices as PDF
  const handleExportPDF = () => {
    try {
      if (selectedInvoices.length === 0) {
        alert('Please select invoices to export');
        return;
      }

      const selectedInvoiceData = invoices.filter(invoice => 
        selectedInvoices.includes(invoice.id)
      );

      if (selectedInvoiceData.length === 0) {
        alert('No invoice data found for export');
        return;
      }

      // Create a new window for PDF generation
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Sent Invoices Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; margin-bottom: 30px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .amount { text-align: right; }
            .status { text-transform: capitalize; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Sent Invoices Report</h1>
          <p>Generated on: ${new Date().toLocaleDateString()}</p>
          <p>Total Invoices: ${selectedInvoiceData.length}</p>
          <table>
            <thead>
              <tr>
                <th>Invoice Number</th>
                <th>Invoice ID</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Due Date</th>
                <th>Sent Date</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              ${selectedInvoiceData.map(invoice => `
                <tr>
                  <td>${invoice.invoiceNumber || ''}</td>
                  <td>${invoice.isGuestInvoice ? (invoice.invoiceId || '') : '—'}</td>
                  <td>${invoice.customer?.name || ''}</td>
                  <td class="amount">£${(parseFloat(invoice.amount) || 0).toFixed(2)}</td>
                  <td class="status">${(invoice.status || '').replace(/-/g, ' ').toUpperCase()}</td>
                  <td>${invoice.dueDate ? formatDate(invoice.dueDate) : ''}</td>
                  <td>${invoice.sentDate ? formatDate(invoice.sentDate) : ''}</td>
                  <td>${invoice.description || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="no-print">
            <p>This report can be printed or saved as PDF using your browser's print function.</p>
            <button onclick="window.print()">Print / Save as PDF</button>
            <button onclick="window.close()">Close</button>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      
      // Clear selection
      setSelectedInvoices([]);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export invoices as PDF. Please try again.');
    }
  };

  // Get status configuration
  const getStatusConfig = (status, refundStatus) => {
    const statusConfigs = {
      pending: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock,
        text: 'PENDING'
      },
      'pending_approval': {
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock,
        text: 'PENDING APPROVAL'
      },
      approved: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        text: 'APPROVED'
      },
      rejected: {
        color: 'bg-red-100 text-red-800',
        icon: XCircle,
        text: 'REJECTED'
      },
      'auto-approved': {
        color: 'bg-blue-100 text-blue-800',
        icon: CheckCircle,
        text: 'AUTO-APPROVED'
      },
      'auto_approved': {
        color: 'bg-blue-100 text-blue-800',
        icon: CheckCircle,
        text: 'AUTO-APPROVED'
      },
      paid: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        text: 'PAID'
      }
    };

    // Determine config based on refund status first, then regular status
    let config;
    if (refundStatus === 'full') {
      config = {
        color: 'bg-red-100 text-red-800',
        icon: RefreshCw,
        text: 'FULLY REFUNDED'
      };
    } else if (refundStatus === 'partial') {
      config = {
        color: 'bg-yellow-100 text-yellow-800',
        icon: RefreshCw,
        text: 'PARTIALLY REFUNDED'
      };
    } else {
      config = statusConfigs[status] || statusConfigs.pending;
    }
    
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        <span className="hidden sm:inline">{config.text}</span>
        <span className="sm:hidden">{config.text.substring(0, 6)}</span>
      </span>
    );
  };

  const handleAction = (type, invoice) => {
    setActionModal({ open: true, type, invoice });
    setSelectedInvoice(invoice);
  };

  const handleResend = async (invoice) => {
    try {
      setLoading(true);
      const response = await api.post(`/invoices/${invoice.id}/resend`);
      
      if (response.data.success) {
        // Update the invoice's sent date and status
        const updatedInvoices = invoices.map(inv => 
          inv.id === invoice.id 
            ? { 
                ...inv, 
                sentDate: response.data.data.invoice.sentDate,
                status: response.data.data.invoice.status,
                lastReminderSent: null
              }
            : inv
        );
        setInvoices(updatedInvoices);
        setFilteredInvoices(updatedInvoices);
        
        alert('Invoice resent successfully!');
      } else {
        alert('Failed to resend invoice: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error resending invoice:', error);
      if (error.response?.status === 400) {
        alert('Invoice cannot be resent in current status: ' + error.response.data.message);
      } else if (error.response?.status === 403) {
        alert('You are not authorized to resend this invoice.');
      } else if (error.response?.status === 404) {
        alert('Invoice not found.');
      } else {
        alert('Error resending invoice. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    // CRITICAL: Only prevent deletion of paid invoices (audit compliance)
    if (invoice.status === 'paid') {
      alert('⛔ Cannot delete paid invoices.\n\nThis invoice has been paid and cannot be deleted for audit and compliance reasons.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete invoice ${invoice.invoiceNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.delete(`/invoices/${invoice.id}`);
      
      if (response.data.success) {
        // Remove invoice from local state
        const updatedInvoices = invoices.filter(inv => inv.id !== invoice.id);
        setInvoices(updatedInvoices);
        setFilteredInvoices(updatedInvoices);
        
        // Remove from selected invoices if it was selected
        setSelectedInvoices(prev => prev.filter(id => id !== invoice.id));
        
        alert('✅ Invoice deleted successfully! The receiver will no longer see this invoice.');
      } else {
        alert('❌ Failed to delete invoice: ' + (response.data.message || response.data.error));
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      if (error.response?.status === 403) {
        alert(`⛔ ${errorMessage || 'You are not authorized to delete this invoice.'}`);
      } else if (error.response?.status === 404) {
        alert('⛔ Invoice not found.');
      } else {
        alert(`❌ ${errorMessage || 'Error deleting invoice. Please try again.'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (invoice) => {
    if (!window.confirm(`Resend invoice email to ${invoice.guestRecipient?.email || invoice.customer?.email}?\n\nThe guest will receive the invoice details and lookup code again.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/invoices/${invoice.id}/resend-email`);
      
      if (response.data.success) {
        alert(`✅ Email resent successfully to ${invoice.guestRecipient?.email || invoice.customer?.email}!`);
      } else {
        alert('❌ ' + (response.data.error || 'Failed to resend email'));
      }
    } catch (error) {
      console.error('Error resending email:', error);
      
      // Handle cooldown error
      if (error.response?.status === 429) {
        const cooldownMinutes = error.response?.data?.cooldownRemaining || 'some';
        alert(`⏳ Please wait ${cooldownMinutes} minute${cooldownMinutes !== 1 ? 's' : ''} before resending.\n\nThis prevents spam and ensures email deliverability.`);
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to resend email';
        alert('❌ ' + errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (invoice) => {
    try {
      setLoading(true);
      const response = await api.post(`/invoices/${invoice.id}/remind`);
      
      if (response.data.success) {
        // Update the invoice's last reminder sent date
        const updatedInvoices = invoices.map(inv => 
          inv.id === invoice.id 
            ? { ...inv, lastReminderSent: new Date().toISOString() }
            : inv
        );
        setInvoices(updatedInvoices);
        setFilteredInvoices(updatedInvoices);
        
        // Update reminder cooldown
        setReminderCooldowns(prev => ({
          ...prev,
          [invoice.id]: 5
        }));
        
        alert('Reminder sent successfully!');
      } else {
        alert('Failed to send reminder: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      if (error.response?.status === 400) {
        alert('Cannot send reminder: ' + error.response.data.message);
      } else if (error.response?.status === 403) {
        alert('You are not authorized to send reminders for this invoice.');
      } else if (error.response?.status === 404) {
        alert('Invoice not found.');
      } else {
        alert('Error sending reminder. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    // Open comprehensive invoice preview modal
    setPreviewInvoice(invoice);
    setShowPreview(true);
  };

  // NEW: Simplified download handlers for InvoicePreview modal
  const handleDownloadPDF = (invoice, options = {}) => {
    console.log('📄 SentInvoices - handleDownloadPDF called with invoice:', invoice);
    handleDownloadInvoice(invoice, 'pdf', options);
  };

  const handleDownloadExcel = (invoice, options = {}) => {
    handleDownloadInvoice(invoice, 'excel', options);
  };

  const legacyHandleDownloadInvoice = async (invoice, format = 'pdf') => {
    try {
      setLoading(true);
      
      if (format === 'pdf') {
        // Generate PDF using browser print functionality
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Invoice ${invoice.invoiceNumber || 'Invoice'}</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
              
              body { 
                font-family: 'Inter', 'Segoe UI', system-ui, sans-serif; 
                margin: 40px; 
                color: #1f2937;
                background: #ffffff;
                line-height: 1.6;
              }
              
              .invoice-container {
                max-width: 800px;
                margin: 0 auto;
                background: white;
                border-radius: 16px;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                overflow: hidden;
              }
              
              .invoice-header {
                text-align: center;
                padding: 40px 30px;
                border-bottom: 1px solid #e2e8f0;
                background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
              }
              
              .invoice-title {
                font-size: 36px;
                font-weight: 300;
                color: #1f2937;
                margin: 0 0 12px 0;
                letter-spacing: 2px;
              }
              
              .title-line {
                width: 80px;
                height: 2px;
                background: #94a3b8;
                margin: 0 auto 24px auto;
              }
              
              .invoice-meta {
                display: flex;
                justify-content: center;
                gap: 40px;
                font-size: 14px;
                color: #64748b;
              }
              
              .meta-item {
                text-align: center;
              }
              
              .meta-label {
                font-size: 11px;
                font-weight: 600;
                color: #94a3b8;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin-bottom: 4px;
              }
              
              .meta-value {
                font-weight: 500;
                color: #475569;
              }
              
              .company-section {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 32px;
                padding: 40px;
              }
              
              .company-card {
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 16px;
                padding: 32px;
              }
              
              .company-header {
                display: flex;
                align-items: center;
                margin-bottom: 24px;
              }
              
              .company-dot {
                width: 8px;
                height: 8px;
                background: #64748b;
                border-radius: 50%;
                margin-right: 12px;
              }
              
              .company-card h3 {
                font-size: 12px;
                font-weight: 600;
                color: #64748b;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin: 0;
              }
              
              .company-card p {
                margin: 6px 0;
                font-size: 14px;
                color: #475569;
                line-height: 1.5;
              }
              
              .company-card p:first-of-type {
                font-weight: 600;
                color: #1e293b;
                font-size: 16px;
                margin-top: 0;
              }
              
              .line-items-section {
                padding: 0 40px 40px 40px;
              }
              
              .line-items-header {
                display: flex;
                align-items: center;
                margin-bottom: 24px;
              }
              
              .line-items-section h3 {
                font-size: 12px;
                font-weight: 600;
                color: #64748b;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin: 0;
              }
              
              .line-items-table {
                background: white;
                border: 1px solid #e2e8f0;
                border-radius: 16px;
                overflow: hidden;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
              }
              
              .line-items-table table {
                width: 100%;
                border-collapse: collapse;
              }
              
              .line-items-table th {
                background: #f8fafc;
                border-bottom: 1px solid #e2e8f0;
                padding: 16px 24px;
                text-align: left;
                font-size: 11px;
                font-weight: 600;
                color: #64748b;
                text-transform: uppercase;
                letter-spacing: 1px;
              }
              
              .line-items-table td {
                padding: 16px 24px;
                border-bottom: 1px solid #f1f5f9;
                font-size: 14px;
                color: #475569;
              }
              
              .line-items-table tr:last-child td {
                border-bottom: none;
              }
              
              .line-items-table .amount {
                text-align: right;
                font-weight: 500;
              }
              
              .empty-state {
                text-align: center;
                padding: 48px 24px;
                color: #94a3b8;
              }
              
              .empty-state p {
                font-size: 14px;
                font-weight: 500;
                margin-bottom: 4px;
              }
              
              .empty-state .subtitle {
                font-size: 12px;
                color: #cbd5e1;
              }
              
              .totals-section {
                padding: 0 40px 40px 40px;
                display: flex;
                justify-content: flex-end;
              }
              
              .totals-card {
                width: 384px;
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 16px;
                padding: 32px;
              }
              
              .total-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px 0;
                font-size: 14px;
              }
              
              .total-row.grand-total {
                border-top: 1px solid #cbd5e1;
                padding-top: 24px;
                margin-top: 16px;
                font-size: 18px;
                font-weight: 600;
                color: #1e293b;
              }
              
              .grand-total .total-amount {
                font-size: 24px;
                font-weight: 700;
              }
              
              .no-print {
                text-align: center;
                padding: 24px;
                background: #f8fafc;
                border-top: 1px solid #e2e8f0;
              }
              
              .no-print button {
                background: #475569;
                color: white;
                border: none;
                padding: 12px 24px;
                border-radius: 8px;
                cursor: pointer;
                margin: 0 8px;
                font-size: 14px;
                font-weight: 500;
              }
              
              .no-print button:hover {
                background: #334155;
              }
              
              @page {
                margin: 0.5in !important;
                size: A4 !important;
              }
              
              @media print {
                * {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                }
                body { 
                  margin: 0 !important; 
                  padding: 0 !important;
                  font-size: 12px;
                  background: white !important;
                }
                html {
                  background: white !important;
                }
                .no-print { display: none !important; }
                .invoice-container { 
                  max-width: none !important;
                  box-shadow: none !important;
                  border-radius: 0 !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }
                .invoice-header { 
                  background: white !important;
                  border-bottom: 1px solid #e2e8f0;
                }
                .company-section { 
                  page-break-inside: avoid;
                }
                .line-items-table { 
                  page-break-inside: avoid;
                }
                .totals-section { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="invoice-container">
              <!-- Invoice Header -->
              <div class="invoice-header">
                <h1 class="invoice-title">INVOICE</h1>
                <div class="title-line"></div>
              <div class="invoice-meta">
                <div class="meta-item">
                  <div class="meta-label">Invoice Date</div>
                  <div class="meta-value">${new Date(invoice.createdAt || invoice.sentDate).toLocaleDateString()}</div>
                </div>
                <div class="meta-item">
                  <div class="meta-label">Invoice Number</div>
                  <div class="meta-value">${invoice.invoiceNumber}</div>
                </div>
                <div class="meta-item">
                  <div class="meta-label">Due Date</div>
                  <div class="meta-value">${new Date(invoice.dueDate).toLocaleDateString()}</div>
                </div>
              </div>
              ${invoice.status ? `
              <div style="margin-top: 16px; text-align: center;">
                <span style="display: inline-block; padding: 6px 16px; background: #fef3c7; color: #92400e; border-radius: 8px; font-size: 11px; font-weight: 600; text-transform: uppercase;">
                  ${invoice.status.replace(/_/g, ' ')}
                </span>
              </div>
              ` : ''}
              ${invoice.poNumber ? `
              <div style="margin-top: 12px; text-align: center; font-size: 12px; color: #64748b;">
                <strong>PO Number:</strong> ${invoice.poNumber}
              </div>
              ` : ''}
              </div>
              
              <!-- Company Information -->
              <div class="company-section">
                <div class="company-card">
                  <div class="company-header">
                    <div class="company-dot"></div>
                    <h3>From</h3>
                  </div>
                  <p><strong>${invoice.sender?.companyName || (invoice.sender?.firstName + ' ' + invoice.sender?.lastName) || '[Company Name]'}</strong></p>
                  <p>${invoice.sender?.firstName || ''} ${invoice.sender?.lastName || ''}</p>
                  ${invoice.sender?.address ? `<p>${invoice.sender.address}</p>` : ''}
                  ${invoice.sender?.city || invoice.sender?.state || invoice.sender?.zipCode ? 
                    `<p>${[invoice.sender?.city, invoice.sender?.state, invoice.sender?.zipCode].filter(Boolean).join(', ')}</p>` 
                    : ''}
                  ${invoice.sender?.country ? `<p>${invoice.sender.country}</p>` : ''}
                  <p>${invoice.sender?.email || '[Email]'}</p>
                </div>
                
                <div class="company-card">
                  <div class="company-header">
                    <div class="company-dot"></div>
                    <h3>To</h3>
                  </div>
                  <p><strong>${invoice.receiver?.companyName || invoice.receiver?.firstName + ' ' + invoice.receiver?.lastName || invoice.customer?.name || '[Company Name]'}</strong></p>
                  <p>${invoice.receiver?.firstName || ''} ${invoice.receiver?.lastName || ''}</p>
                  ${invoice.receiver?.address || invoice.customer?.address ? `<p>${invoice.receiver?.address || invoice.customer?.address}</p>` : ''}
                  ${invoice.receiver?.city || invoice.receiver?.state || invoice.receiver?.zipCode || invoice.customer?.city || invoice.customer?.zipCode ? 
                    `<p>${[invoice.receiver?.city || invoice.customer?.city, invoice.receiver?.state, invoice.receiver?.zipCode || invoice.customer?.zipCode].filter(Boolean).join(', ')}</p>` 
                    : ''}
                  ${invoice.receiver?.country || invoice.customer?.country ? `<p>${invoice.receiver?.country || invoice.customer?.country}</p>` : ''}
                  <p>${invoice.receiver?.email || invoice.customer?.email || '[Email]'}</p>
                </div>
              </div>
              
              <!-- Line Items -->
              <div class="line-items-section">
                <div class="line-items-header">
                  <div class="company-dot"></div>
                  <h3>Line Items</h3>
                </div>
                <div class="line-items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th style="text-align: center;">Qty</th>
                        <th style="text-align: right;">Unit Price</th>
                        <th style="text-align: right;">Tax %</th>
                        <th style="text-align: right;">Discount %</th>
                        <th style="text-align: right;">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${invoice.lineItems && invoice.lineItems.length > 0 ? 
                        invoice.lineItems.map(item => {
                          const subtotal = (item.quantity || 0) * (item.unitPrice || 0);
                          const discount = subtotal * ((item.discount || 0) / 100);
                          const afterDiscount = subtotal - discount;
                          const tax = afterDiscount * ((item.taxRate || 0) / 100);
                          const total = afterDiscount + tax;
                          const formattedUnitPrice = (item.unitPrice || 0) < 0 ? `(£${Math.abs(item.unitPrice || 0).toFixed(2)})` : `£${(item.unitPrice || 0).toFixed(2)}`;
                          const formattedTotal = total < 0 ? `(£${Math.abs(total).toFixed(2)})` : `£${total.toFixed(2)}`;
                          return `
                            <tr>
                              <td>${item.description || '[Item description]'}</td>
                              <td style="text-align: center;">${item.quantity || 0}</td>
                              <td class="amount">${formattedUnitPrice}</td>
                              <td style="text-align: right;">${item.taxRate || 0}%</td>
                              <td style="text-align: right;">${item.discount || 0}%</td>
                              <td class="amount">${formattedTotal}</td>
                            </tr>
                          `;
                        }).join('') : 
                        '<tr><td colspan="6"><div class="empty-state"><p>No line items found</p><div class="subtitle">Add items to this invoice</div></div></td></tr>'
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              
              <!-- Totals -->
              <div class="totals-section">
                <div class="totals-card">
                  <div class="total-row">
                    <span style="color: #64748b;">Subtotal:</span>
                    <span style="font-weight: 500;">£${(parseFloat(invoice.subtotal) || 0).toFixed(2)}</span>
                  </div>
                  <div class="total-row">
                    <span style="color: #64748b;">Tax:</span>
                    <span style="font-weight: 500;">£${(parseFloat(invoice.tax) || 0).toFixed(2)}</span>
                  </div>
                  ${invoice.discountAmount && parseFloat(invoice.discountAmount) > 0 ? `
                  <div class="total-row" style="background: #f9f9f9; margin: 8px -16px; padding: 8px 16px; border-top: 1px dashed #999; border-bottom: 1px dashed #999;">
                    <span style="color: #555; font-style: italic;">
                      ✨ Early Payment Discount
                      ${invoice.discountSourceInvoice?.invoiceNumber ? `<br><span style="font-size: 10px; font-style: normal;">(from ${invoice.discountSourceInvoice.invoiceNumber})</span>` : ''}:
                    </span>
                    <span style="color: #555; font-style: italic; font-weight: 500;">-£${(parseFloat(invoice.discountAmount)).toFixed(2)}</span>
                  </div>
                  ` : ''}
                  <div class="total-row grand-total">
                    <span>Total:</span>
                    <span class="total-amount">£${(parseFloat(invoice.amount) || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              ${invoice.notes ? `
              <div style="padding: 0 40px 20px 40px;">
                <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
                  <h4 style="font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; margin: 0 0 12px 0;">Notes:</h4>
                  <p style="margin: 0; font-size: 13px; color: #475569; line-height: 1.6;">${invoice.notes}</p>
                </div>
              </div>
              ` : ''}
              
              ${invoice.terms ? `
              <div style="padding: 0 40px 20px 40px;">
                <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
                  <h4 style="font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; margin: 0 0 12px 0;">Terms & Conditions:</h4>
                  <p style="margin: 0; font-size: 13px; color: #475569; line-height: 1.6;">${invoice.terms}</p>
                </div>
              </div>
              ` : ''}
              
              <div class="no-print">
                <p>This invoice can be printed or saved as PDF using your browser's print function.</p>
                <button onclick="window.print()">Print / Save as PDF</button>
                <button onclick="window.close()">Close</button>
              </div>
            </div>
          </body>
          </html>
        `);
        printWindow.document.close();
      } else if (format === 'csv') {
        // Generate CSV
        const csvContent = [
          ['Invoice Number', 'Customer', 'Amount', 'Status', 'Due Date', 'Sent Date'],
          [
            invoice.invoiceNumber || '',
            invoice.customer?.name || invoice.receiver?.companyName || invoice.receiver?.firstName + ' ' + invoice.receiver?.lastName || '',
            `£${(invoice.amount || 0).toFixed(2)}`,
            (invoice.status || '').replace(/-/g, ' ').toUpperCase(),
            invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '',
            invoice.sentDate ? new Date(invoice.sentDate).toLocaleDateString() : ''
          ]
        ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sent-invoice-${invoice.invoiceNumber || 'unknown'}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      
      alert(`Successfully downloaded invoice as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download invoice as ${format.toUpperCase()}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice, format = 'pdf', extraOptions = {}) => {
    try {
      await downloadInvoice(
        invoice,
        format === 'excel' ? 'csv' : format,
        { setLoading, ...extraOptions }
      );
      alert(`Successfully downloaded invoice as ${format.toUpperCase()}`);
    } catch (error) {
      console.warn('Invoice API download failed, using legacy renderer', error);
      try {
        await legacyHandleDownloadInvoice(invoice, format);
      } catch (legacyError) {
        console.error('Download error:', legacyError);
        alert(`Failed to download invoice as ${format.toUpperCase()}. Please try again.`);
      }
    }
  };

  const handleEditInvoice = (invoice) => {
    // Navigate to edit invoice page
    window.location.href = `/portal/edit-invoice/${invoice.id}`;
  };

  const handleDuplicateInvoice = async (invoice) => {
    try {
      setLoading(true);
      const response = await api.post(`/invoices/${invoice.id}/duplicate`);
      
      if (response.data.success) {
        alert('Invoice duplicated successfully!');
        // Refresh the list
        fetchSentInvoices();
      } else {
        alert('Failed to duplicate invoice: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error duplicating invoice:', error);
      alert('Error duplicating invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveInvoice = async (invoice) => {
    try {
      setLoading(true);
      const response = await api.post(`/invoices/${invoice.id}/archive`);
      
      if (response.data.success) {
        // Update the invoice's status
        const updatedInvoices = invoices.map(inv => 
          inv.id === invoice.id 
            ? { ...inv, status: 'archived' }
            : inv
        );
        setInvoices(updatedInvoices);
        setFilteredInvoices(updatedInvoices);
        
        alert('Invoice archived successfully!');
      } else {
        alert('Failed to archive invoice: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error archiving invoice:', error);
      alert('Error archiving invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePinInvoice = async (invoice) => {
    try {
      setLoading(true);
      const response = await api.post(`/invoices/${invoice.id}/pin`);
      
      if (response.data.success) {
        // Update the invoice's pinned status
        const updatedInvoices = invoices.map(inv => 
          inv.id === invoice.id 
            ? { ...inv, pinned: !inv.pinned }
            : inv
        );
        setInvoices(updatedInvoices);
        setFilteredInvoices(updatedInvoices);
        
        alert(invoice.pinned ? 'Invoice unpinned successfully!' : 'Invoice pinned successfully!');
      } else {
        alert('Failed to pin invoice: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error pinning invoice:', error);
      alert('Error pinning invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount) => {
    // Convert pence to pounds (amount is stored in pence in database)
    const amountInPounds = amount;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amountInPounds);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Sent Invoices</h1>
          <p className="text-gray-600">Track and manage invoices you've sent to customers</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchSentInvoices}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          {selectedInvoices.length > 0 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleExportSelected}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV ({selectedInvoices.length})
              </button>
              <button
                onClick={handleExportPDF}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export PDF ({selectedInvoices.length})
              </button>
            </div>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by Invoice, Customer, or Email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="auto-approved">Auto-Approved</option>
              <option value="paid">Paid</option>
            </select>
          </div>

          {/* Clear Filters */}
          <div>
            <button
              onClick={clearFilters}
              className="w-full px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-gray-200">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Amount Range</label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={amountRange.min}
                  onChange={(e) => setAmountRange(prev => ({ ...prev, min: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={amountRange.max}
                  onChange={(e) => setAmountRange(prev => ({ ...prev, max: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-sm"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading invoices...</span>
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery || statusFilter !== 'all' || dateRange.start || dateRange.end || amountRange.min || amountRange.max
                ? 'Try adjusting your filters or search terms.'
                : 'Get started by creating your first invoice.'}
            </p>
            {!searchQuery && statusFilter === 'all' && !dateRange.start && !dateRange.end && !amountRange.min && !amountRange.max && (
              <div className="mt-6">
                <button
                  onClick={() => window.location.href = '/portal/send-invoice'}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedInvoices.length === currentInvoices.length && currentInvoices.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {selectedInvoices.length > 0 ? `${selectedInvoices.length} selected` : 'Select all'}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  {filteredInvoices.length} of {invoices.length} invoices
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentInvoices.map((invoice) => (
                    <tr key={invoice.id} className={`hover:bg-gray-50 ${
                      highlightedInvoiceId === invoice.id 
                        ? 'bg-blue-50 border-l-4 border-blue-500' 
                        : ''
                    }`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedInvoices.includes(invoice.id)}
                            onChange={() => handleInvoiceSelect(invoice.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</span>
                              {invoice.creator && (
                                <span 
                                  className="inline-flex items-center justify-center w-6 h-6 text-xs font-semibold text-blue-600 bg-blue-100 rounded-full" 
                                  title={`Created by ${invoice.creator.firstName} ${invoice.creator.lastName}`}
                                >
                                  {invoice.creator.initials}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{formatDate(invoice.sentDate)}</div>
                            {invoice.poNumber && (
                              <div className="text-xs text-blue-600 font-medium">
                                PO: {invoice.poNumber}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {invoice.isGuestInvoice ? (
                          <>
                            <div className="text-sm font-medium text-blue-600 font-mono text-lg tracking-wider">
                              {invoice.guestLookupCode || invoice.invoiceId}
                            </div>
                            <div className="text-xs text-gray-500">Guest lookup code</div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-400">—</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{invoice.customer.name}</div>
                          <div className="text-sm text-gray-500">{invoice.customer.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 mb-2">{formatCurrency(invoice.amount)}</div>
                        {/* Early Payment Badge (Compact) */}
                        <EarlyPaymentBanner 
                          invoice={invoice} 
                          viewType="sender" 
                          compact={true}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusConfig(invoice.status, invoice.refundStatus)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {/* Only show due date for non-rejected/non-disputed invoices */}
                        {invoice.status !== 'rejected' && invoice.status !== 'disputed' ? (
                          <>
                            <div className="text-sm text-gray-900">{formatDate(invoice.dueDate)}</div>
                            {invoice.dueDate && new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid' && (
                              <div className="text-xs text-red-600">Overdue</div>
                            )}
                          </>
                        ) : (
                          <div className="text-sm text-gray-400">—</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {/* View Invoice */}
                          <button
                            onClick={() => handleViewInvoice(invoice)}
                            className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                            title="View Invoice"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* Resend Email Button - Guest Invoices Only */}
                          {invoice.isGuestInvoice && (
                            <button
                              onClick={() => handleResendEmail(invoice)}
                              className="text-green-600 hover:text-green-800 transition-colors duration-200"
                              title="Resend Email"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}

                          {/* Delete Invoice Button */}
                          {invoice.status !== 'paid' && (
                            <div className="relative">
                              <button
                                onClick={() => handleDeleteInvoice(invoice)}
                                className="text-red-400 hover:text-red-600 transition-colors duration-200"
                                title="Delete Invoice"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredInvoices.length)} of {filteredInvoices.length} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 text-sm rounded-md ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Action Modal */}
      {actionModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {actionModal.type === 'preview' ? 'Invoice Preview' : 'Confirm Action'}
              </h3>
              
              {actionModal.type === 'preview' ? (
                <div className="text-left">
                  <div className="mb-4">
                    <strong>Invoice Number:</strong> {actionModal.invoice.invoiceNumber}
                  </div>
                  <div className="mb-4">
                    <strong>Customer:</strong> {actionModal.invoice.customer.name}
                  </div>
                  <div className="mb-4">
                    <strong>Amount:</strong> {formatCurrency(actionModal.invoice.amount)}
                  </div>
                  <div className="mb-4">
                    <strong>Status:</strong> {getStatusConfig(actionModal.invoice.status, actionModal.invoice.refundStatus)}
                  </div>
                  <div className="mb-4">
                    <strong>Due Date:</strong> {formatDate(actionModal.invoice.dueDate)}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to {actionModal.type} this invoice?
                </p>
              )}
              
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setActionModal({ open: false, type: '', invoice: null })}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors duration-200"
                >
                  Close
                </button>
                {actionModal.type !== 'preview' && (
                  <button
                    onClick={() => {
                      // Handle the action based on type
                      setActionModal({ open: false, type: '', invoice: null });
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
                  >
                    Confirm
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal with Early Payment Banner */}
      {showPreview && previewInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              {/* Early Payment Banner (Full) */}
              <EarlyPaymentBanner 
                invoice={previewInvoice} 
                viewType="sender" 
                compact={false}
              />
              
              {/* Invoice Preview */}
              <InvoicePreview
                invoice={previewInvoice}
                isOpen={true}
                onClose={() => {
                  setShowPreview(false);
                  setPreviewInvoice(null);
                }}
                onDownloadPDF={(invoice) => handleDownloadPDF(invoice, { forcePrintablePreview: true })}
                onDownloadExcel={handleDownloadExcel}
                showDownloadButtons={true}
                title="Sent Invoice Details"
                hideModal={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentInvoices;
